import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { workspaceApi, formatApiError, llmApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Loader2, Save, Plus, Trash2, Key, Users, Eye, EyeOff, UserPlus } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/page-shell";
import { toast } from "sonner";

/* These are exactly the thirteen ids the backend routes (BUILTIN_LLM_PROVIDERS in
   server.py). Keep the two lists in step: a key saved for an id the backend
   does not know is stored and then silently ignored, which is how "qwen" used
   to sit here doing nothing. Anything else belongs in Custom providers below,
   which is a real, supported route. */
const LLM_PROVIDERS = [
  { value: "openrouter", label: "OpenRouter", placeholder: "sk-or-...", prefix: "sk-or-", consoleUrl: "https://openrouter.ai/keys",
    note: "One key, hundreds of models from most major labs." },
  { value: "openai",    label: "OpenAI",        placeholder: "sk-...",     prefix: "sk-",     consoleUrl: "https://platform.openai.com/api-keys" },
  { value: "anthropic", label: "Anthropic",     placeholder: "sk-ant-...", prefix: "sk-ant-", consoleUrl: "https://console.anthropic.com/settings/keys" },
  { value: "gemini",    label: "Google Gemini", placeholder: "AIza...",    prefix: "AIza",    consoleUrl: "https://aistudio.google.com/app/apikey" },
  { value: "deepseek",  label: "DeepSeek",      placeholder: "sk-...",     prefix: "sk-",     consoleUrl: "https://platform.deepseek.com/api_keys" },
  { value: "grok",      label: "xAI Grok",      placeholder: "xai-...",    prefix: "xai-",    consoleUrl: "https://console.x.ai/" },
  { value: "groq",      label: "Groq",          placeholder: "gsk_...",    prefix: "gsk_",    consoleUrl: "https://console.groq.com/keys",
    note: "Open-weight models, very fast and cheap." },
  { value: "together",  label: "Together AI",   placeholder: "...",        prefix: "",        consoleUrl: "https://api.together.ai/settings/api-keys",
    note: "Llama, Mixtral, Qwen and other open-weight models." },
  { value: "1minai",    label: "1min.AI",       placeholder: "...",        prefix: "",        consoleUrl: "https://app.1min.ai/" },
  { value: "qwen",      label: "Alibaba Qwen (Model Studio)", placeholder: "sk-...", prefix: "",
    note: "Qwen3.7 Flash, Qwen3.8 Flash and Qwen3.7 Plus. Use a key from the Singapore (International) region." },
  { value: "zai",       label: "Z.ai GLM",      placeholder: "...",        prefix: "",
    note: "GLM models from Z.ai." },
  { value: "moonshot",  label: "Moonshot Kimi", placeholder: "sk-...",     prefix: "",        consoleUrl: "https://platform.moonshot.ai/",
    note: "Kimi models from Moonshot AI." },
  { value: "mistral",   label: "Mistral AI",    placeholder: "...",        prefix: "",        consoleUrl: "https://console.mistral.ai/",
    note: "Mistral Small and other Mistral models." },
];

const LOCAL_LLM_PRESETS = [
  { key: "ollama",    label: "Ollama",     id: "ollama-local",    url: "http://localhost:11434/v1", priv: true },
  { key: "lmstudio",  label: "LM Studio",  id: "lmstudio-local",  url: "http://localhost:1234/v1",  priv: true },
  { key: "vllm",      label: "vLLM",       id: "vllm-local",      url: "http://localhost:8000/v1",  priv: true },
];

/* Settings renders this page one section at a time, inside its own tab bar.
   `embedded` drops the page header and the internal tabs; `section` picks
   which one shows. Used standalone it behaves exactly as it always has. */
export default function WorkspaceManage({ embedded = false, section = "general" }) {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  /* OpenRouter first: one key reaches most models, which is the fastest
     route to a working assistant. */
  const [newKeyProvider, setNewKeyProvider] = useState("openrouter");
  const [showKey, setShowKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState("");
  const [showKeys, setShowKeys] = useState({});
  const [customProviders, setCustomProviders] = useState([]);
  const [npId, setNpId] = useState("");
  const [npLabel, setNpLabel] = useState("");
  const [npBaseUrl, setNpBaseUrl] = useState("");
  const [npAllowPrivate, setNpAllowPrivate] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [testingProvider, setTestingProvider] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [localLlmAllowed, setLocalLlmAllowed] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadProviders = async () => {
    try {
      const res = await llmApi.listProviders();
      setCustomProviders(res.data?.custom || []);
      /* Absent on older backends; assume allowed so this never hides a
         feature someone is already paying for. */
      setLocalLlmAllowed(res.data?.local_llm_allowed !== false);
    } catch (e) {
      setCustomProviders([]);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const handleAddProvider = async () => {
    if (!npId.trim() || !npBaseUrl.trim()) return;
    setSavingProvider(true);
    try {
      await llmApi.upsertProvider({ id: npId.trim(), label: npLabel.trim(), base_url: npBaseUrl.trim(), allow_private: npAllowPrivate });
      toast.success(`Provider "${npId.trim()}" saved`);
      setNpId("");
      setNpLabel("");
      setNpBaseUrl("");
      setNpAllowPrivate(false);
      await loadProviders();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingProvider(false);
    }
  };

  const handleTestProvider = async () => {
    if (!npBaseUrl.trim()) return;
    setTestingProvider(true);
    setTestResult(null);
    try {
      const res = await llmApi.testProvider({
        base_url: npBaseUrl.trim(),
        allow_private: npAllowPrivate,
      });
      setTestResult(res.data);
      if (res.data?.suggested_base_url) setNpBaseUrl(res.data.suggested_base_url);
    } catch (e) {
      setTestResult({ ok: false, detail: formatApiError(e.response?.data?.detail) });
    } finally {
      setTestingProvider(false);
    }
  };

  const handleDeleteProvider = async (id) => {
    if (!window.confirm(`Delete provider "${id}"?`)) return;
    try {
      await llmApi.deleteProvider(id);
      toast.success(`Provider "${id}" deleted`);
      await loadProviders();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadData = async () => {
    try {
      const [wsRes, teamRes] = await Promise.all([
        workspaceApi.get(),
        workspaceApi.getTeam()
      ]);
      setWorkspace(wsRes.data);
      setName(wsRes.data.name);
      setTeam(teamRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    setSaving(true);
    try {
      await workspaceApi.update({ name });
      toast.success("Workspace name updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleAddApiKey = async () => {
    if (!newKeyValue.trim()) return;
    setSavingKey(true);
    try {
      const res = await workspaceApi.updateApiKey(newKeyProvider, newKeyValue);
      setWorkspace(prev => ({
        ...prev,
        api_keys_masked: { ...prev.api_keys_masked, [newKeyProvider]: res.data.masked_key }
      }));
      setNewKeyValue("");
      toast.success(`${newKeyProvider} API key added`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteApiKey = async (provider) => {
    if (!window.confirm(`Delete ${provider} API key?`)) return;
    try {
      await workspaceApi.deleteApiKey(provider);
      setWorkspace(prev => {
        const keys = { ...prev.api_keys_masked };
        delete keys[provider];
        return { ...prev, api_keys_masked: keys };
      });
      toast.success("API key deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await workspaceApi.inviteTeamMember(inviteEmail, inviteRole);
      setInviteOpen(false);
      setInviteEmail("");
      toast.success(`Invitation sent to ${inviteEmail}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await workspaceApi.removeTeamMember(memberId);
      setTeam(team.filter(m => m.id !== memberId));
      toast.success("Team member removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workspace-manage">
      {!embedded && (
        <div>
          <PageHeader title="API & Team Management" subtitle="Generate API keys, manage teammates, and configure your workspace." />
        </div>
      )}

      <Tabs
        {...(embedded ? { value: section } : { defaultValue: "general" })}
        className="space-y-6"
      >
        {!embedded && (
          <TabsList className="grid w-full max-w-md grid-cols-3 rounded-none">
            <TabsTrigger value="general" className="rounded-none">General</TabsTrigger>
            <TabsTrigger value="api-keys" className="rounded-none">API Keys</TabsTrigger>
            <TabsTrigger value="team" className="rounded-none">Team</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="general" className="space-y-6">
          <div className="border border-border p-6 space-y-6">
            <h2 className="font-semibold">Workspace Information</h2>
            <div className="space-y-2 max-w-md">
              <Label className="overline">Workspace Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-none"
                data-testid="workspace-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="overline">Plan</Label>
              <p className="font-medium capitalize">{workspace?.plan}</p>
            </div>
            <Button onClick={handleSaveName} disabled={saving} className="rounded-none" data-testid="save-workspace-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="api-keys" className="space-y-6">
          <div className="border border-border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold">LLM API Keys</h2>
                <p className="text-sm text-muted-foreground">Configure API keys for different LLM providers</p>
              </div>
            </div>

            {/* Existing Keys */}
            {(!workspace?.api_keys_masked || Object.keys(workspace.api_keys_masked).length === 0) && (
              <div className="border border-dashed rounded-md p-6 text-center text-sm text-muted-foreground" data-testid="empty-keys-state">
                <Key className="w-5 h-5 mx-auto mb-2 opacity-60" />
                No API keys yet. Bring your own account and Bicarra will use it.
                One OpenRouter key reaches most models from most major labs;
                add a provider directly if you already have an account with them.
              </div>
            )}
            {workspace?.api_keys_masked && Object.keys(workspace.api_keys_masked).length > 0 && (
              <div className="border border-border divide-y divide-border">
                {Object.entries(workspace.api_keys_masked).map(([provider, masked]) => (
                  <div key={provider} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Key className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium capitalize">{provider}</p>
                        <p className="text-sm text-muted-foreground font-mono">{masked}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteApiKey(provider)}
                      className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--signal-red))]"
                      data-testid={`delete-key-${provider}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Key */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-medium">Add API Key</h3>
                {(() => {
                  const sel = LLM_PROVIDERS.find(p => p.value === newKeyProvider);
                  return sel?.consoleUrl ? (
                    <a href={sel.consoleUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-b2-indigo hover:underline" data-testid="provider-console-link">
                      Get a {sel.label} key →
                    </a>
                  ) : null;
                })()}
              </div>
              {(() => {
                const sel = LLM_PROVIDERS.find(p => p.value === newKeyProvider);
                return sel?.note
                  ? <p className="text-xs text-b2-muted -mt-1" data-testid="provider-note">{sel.note}</p>
                  : null;
              })()}
              <div className="flex gap-2">
                <Select value={newKeyProvider} onValueChange={(v) => { setNewKeyProvider(v); setNewKeyValue(""); }}>
                  <SelectTrigger className="w-[180px] rounded-none" data-testid="provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LLM_PROVIDERS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="relative flex-1">
                  <Input
                    value={newKeyValue}
                    onChange={(e) => setNewKeyValue(e.target.value)}
                    placeholder={LLM_PROVIDERS.find(p => p.value === newKeyProvider)?.placeholder || "sk-..."}
                    type={showKey ? "text" : "password"}
                    className="rounded-none pr-10 font-mono"
                    autoComplete="off"
                    spellCheck={false}
                    data-testid="api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showKey ? "Hide key" : "Show key"}
                    data-testid="toggle-show-key"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button onClick={handleAddApiKey} disabled={!newKeyValue.trim() || savingKey} className="rounded-none" data-testid="add-api-key-btn">
                  {savingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {savingKey ? "Saving..." : "Add"}
                </Button>
              </div>
              {(() => {
                const sel = LLM_PROVIDERS.find(p => p.value === newKeyProvider);
                if (!sel?.prefix || !newKeyValue.trim()) return null;
                if (newKeyValue.trim().startsWith(sel.prefix)) return null;
                return (
                  <p className="text-xs text-amber-600" data-testid="key-format-warning">
                    Heads up: {sel.label} keys usually start with <code className="font-mono">{sel.prefix}</code>. Double-check before saving.
                  </p>
                );
              })()}
              <p className="text-xs text-muted-foreground">
                Encrypted at rest with Fernet (AES-128-CBC + HMAC-SHA256) using a dedicated key separate from your session token. Bicarra staff cannot view your raw keys. You can delete them anytime.
              </p>
            </div>

            {/* Custom AI Providers */}
            <div className="border border-border p-6 space-y-6">
              <div>
                <h2 className="font-semibold">Custom AI Providers</h2>
                <p className="text-sm text-muted-foreground">
                  Register any OpenAI-compatible endpoint, including a model you host yourself.
                </p>
              </div>

              {!localLlmAllowed && (
                <div
                  data-testid="local-llm-locked"
                  className="rounded-[10px] bg-b2-attentionwash border border-b2-attention/25 px-4 py-3"
                >
                  <p className="text-[13.5px] font-medium text-b2-attention">
                    Self-hosted models are on the paid plans
                  </p>
                  <p className="mt-1 text-[13px] text-b2-inksoft">
                    Pointing an assistant at your own model — Ollama, LM Studio, vLLM or any
                    OpenAI-compatible endpoint — is included from the Starter plan onwards.
                    The hosted providers above work on every plan.
                  </p>
                  <Link to="/settings/plan" className="mt-2 inline-block text-[13px] font-medium text-b2-indigo hover:underline">
                    See plans →
                  </Link>
                </div>
              )}

              {customProviders.length > 0 && (
                <div className="border border-border divide-y divide-border">
                  {customProviders.map(cp => (
                    <div key={cp.id} className="p-4 flex items-center justify-between" data-testid={`custom-provider-row-${cp.id}`}>
                      <div className="flex items-center gap-3">
                        <Key className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{cp.label || cp.id} <span className="text-sm text-muted-foreground font-mono">({cp.id})</span></p>
                          <p className="text-sm text-muted-foreground font-mono">{cp.base_url}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteProvider(cp.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--signal-red))]"
                        data-testid={`custom-provider-delete-${cp.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {customProviders.length === 0 && (
                <div className="border border-dashed rounded-md p-6 text-center text-sm text-muted-foreground" data-testid="empty-custom-providers-state">
                  No custom providers yet. Add one below to point Bicarra at your own OpenAI-compatible endpoint.
                </div>
              )}

              {/* Add Custom Provider */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Add Custom Provider</h3>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Quick fill for a local model:</p>
                  <div className="flex gap-2 flex-wrap">
                    {LOCAL_LLM_PRESETS.map(p => (
                      <Button
                        key={p.key}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => { setNpId(p.id); setNpBaseUrl(p.url); setNpAllowPrivate(p.priv); setTestResult(null); }}
                        data-testid={`custom-provider-preset-${p.key}`}
                      >
                        {p.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input
                    value={npId}
                    onChange={(e) => setNpId(e.target.value)}
                    placeholder="id, e.g. ollama"
                    className="rounded-none font-mono"
                    data-testid="custom-provider-id"
                  />
                  <Input
                    value={npLabel}
                    onChange={(e) => setNpLabel(e.target.value)}
                    placeholder="Label, e.g. Ollama (local)"
                    className="rounded-none"
                    data-testid="custom-provider-label"
                  />
                  <Input
                    value={npBaseUrl}
                    onChange={(e) => setNpBaseUrl(e.target.value)}
                    placeholder="Base URL, e.g. http://localhost:11434/v1"
                    className="rounded-none font-mono"
                    data-testid="custom-provider-baseurl"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="custom-provider-allow-private">
                    <input
                      type="checkbox"
                      checked={npAllowPrivate}
                      onChange={(e) => setNpAllowPrivate(e.target.checked)}
                      className="h-4 w-4 accent-[hsl(var(--klein-blue))]"
                    />
                    Allow private/self-hosted address (Ollama, LM Studio)
                  </label>
                  <Button onClick={handleAddProvider} disabled={!npId.trim() || !npBaseUrl.trim() || savingProvider} className="rounded-none" data-testid="custom-provider-add">
                    {savingProvider ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    {savingProvider ? "Saving..." : "Add Provider"}
                  </Button>
                  <Button
                    onClick={handleTestProvider}
                    disabled={!npBaseUrl.trim() || testingProvider}
                    className="rounded-none"
                    data-testid="custom-provider-test"
                  >
                    {testingProvider ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {testingProvider ? "Testing..." : "Test connection"}
                  </Button>
                </div>
                {testResult && (
                  testResult.ok ? (
                    <div className="text-xs text-green-600" data-testid="custom-provider-test-result">
                      <p>Connected. Found {testResult.count} models{testResult.sample?.length ? ": " + testResult.sample.join(", ") : ""}</p>
                      {testResult.note && <p>{testResult.note}</p>}
                    </div>
                  ) : (
                    <div className="text-xs text-amber-600" data-testid="custom-provider-test-result">
                      <p>{testResult.detail}</p>
                    </div>
                  )
                )}
                <p className="text-xs text-muted-foreground">
                  Any OpenAI-compatible endpoint works. After adding a provider, save its API key above using the same id, then pick it on a chatbot. To use a model running on your own machine, expose it with a tunnel and paste the https URL here, for example: <code>cloudflared tunnel --url http://localhost:11434</code> — the tunnel gives a public https address, so you do not need to tick the private-address option.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="team" className="space-y-6">
          <div className="border border-border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold">Team Members</h2>
                <p className="text-sm text-muted-foreground">{team.length} member{team.length !== 1 ? 's' : ''}</p>
              </div>
              <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-none" data-testid="invite-member-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Invite
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                  <DialogHeader>
                    <DialogTitle>Invite Team Member</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label className="overline">Email</Label>
                      <Input
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="colleague@company.com"
                        className="rounded-none"
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="overline">Role</Label>
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger className="rounded-none">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleInvite} className="w-full rounded-none" data-testid="send-invite-btn">
                      Send Invitation
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-border divide-y divide-border">
              {team.map(member => (
                <div key={member.id} className="p-4 flex items-center justify-between" data-testid={`team-member-${member.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-muted flex items-center justify-center font-medium">
                      {member.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`status-badge ${member.role === 'admin' ? 'bg-[hsl(var(--klein-blue))] text-white' : 'status-untrained'}`}>
                      {member.role}
                    </span>
                    {member.id !== user?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveMember(member.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--signal-red))]"
                        data-testid={`remove-member-${member.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
